import { byId, isAr, formatCurrency, formatNumber, attachCopyButton, setupLiveCalculation, detectUserCurrency } from './calc-common.js';
import Big from 'big.js';
import { getInvoiceTafgeet } from './invoice-currencies.js';

export function calculateZakat({
  cash = 0,
  gold24k = 0,
  gold21k = 0,
  gold18k = 0,
  gold24kPrice = 0,
  silverGrams = 0,
  silverPrice = 0,
  tradeInventory = 0,
  receivables = 0,
  liabilities = 0,
  calendarType = 'lunar' // 'lunar' (2.5%) | 'solar' (2.577%)
}) {
  const rate = calendarType === 'solar' ? Big('0.02577') : Big('0.025');

  // Convert gold weights to 24k pure gold equivalent value
  // 21k = 21/24 = 0.875; 18k = 18/24 = 0.75
  const g24Weight = Big(gold24k);
  const g21Weight = Big(gold21k).times(21).div(24);
  const g18Weight = Big(gold18k).times(18).div(24);
  const totalPureGoldWeight = g24Weight.plus(g21Weight).plus(g18Weight);

  const goldValue = totalPureGoldWeight.times(Big(gold24kPrice));
  const silverValue = Big(silverGrams).times(Big(silverPrice));

  const grossAssets = Big(cash)
    .plus(goldValue)
    .plus(silverValue)
    .plus(Big(tradeInventory))
    .plus(Big(receivables));

  const netZakatable = grossAssets.minus(Big(liabilities));
  const safeNetZakatable = netZakatable.gt(0) ? netZakatable : Big(0);

  // Nisab based on 85g 24k gold
  const nisab = Big(85).times(Big(gold24kPrice));
  const isEligible = safeNetZakatable.gte(nisab) && nisab.gt(0);

  const zakatDue = isEligible ? safeNetZakatable.times(rate) : Big(0);

  return {
    grossAssets: Number(grossAssets.toFixed(2)),
    goldValue: Number(goldValue.toFixed(2)),
    silverValue: Number(silverValue.toFixed(2)),
    netZakatable: Number(safeNetZakatable.toFixed(2)),
    nisab: Number(nisab.toFixed(2)),
    isEligible,
    ratePercent: calendarType === 'solar' ? 2.577 : 2.5,
    zakatDue: Number(zakatDue.toFixed(2))
  };
}

export function initZakatCalculator() {
  const container = byId('zakat-calculator-app');
  if (!container) return;

  const curSelect = byId('zakat-currency');
  if (curSelect) {
    curSelect.value = detectUserCurrency('SAR');
  }

  const inputIds = [
    'zakat-cash',
    'zakat-gold-price',
    'zakat-gold-24',
    'zakat-gold-21',
    'zakat-gold-18',
    'zakat-silver-price',
    'zakat-silver-grams',
    'zakat-trade-goods',
    'zakat-debts-owed-to-you',
    'zakat-debts-you-owe',
    'zakat-calendar-type',
    'zakat-currency'
  ];

  setupLiveCalculation(inputIds, runZakatCalc);

  attachCopyButton(byId('zakat-copy-btn'), () => {
    const total = byId('zakat-due-result')?.textContent || '';
    const status = byId('zakat-eligibility-badge')?.textContent || '';
    const net = byId('zakat-net-assets')?.textContent || '';
    const nisab = byId('zakat-nisab-val')?.textContent || '';
    return isAr
      ? `📋 ملخص حساب الزكاة الشرعية:\n• مقدار الزكاة الواجبة: ${total}\n• حالة النصاب: ${status}\n• صافي الوعاء الزكوي: ${net}\n• نصاب الذهب (85 جم): ${nisab}`
      : `📋 Islamic Zakat Calculation Summary:\n• Zakat Due: ${total}\n• Nisab Status: ${status}\n• Net Zakatable Assets: ${net}\n• Gold Nisab (85g): ${nisab}`;
  });

  const printBtn = byId('zakat-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  runZakatCalc();
}

function runZakatCalc() {
  const currency = byId('zakat-currency')?.value || 'SAR';
  const cash = parseFloat(byId('zakat-cash')?.value) || 0;
  const gold24kPrice = parseFloat(byId('zakat-gold-price')?.value) || 0;
  const gold24k = parseFloat(byId('zakat-gold-24')?.value) || 0;
  const gold21k = parseFloat(byId('zakat-gold-21')?.value) || 0;
  const gold18k = parseFloat(byId('zakat-gold-18')?.value) || 0;
  const silverPrice = parseFloat(byId('zakat-silver-price')?.value) || 0;
  const silverGrams = parseFloat(byId('zakat-silver-grams')?.value) || 0;
  const tradeInventory = parseFloat(byId('zakat-trade-goods')?.value) || 0;
  const receivables = parseFloat(byId('zakat-debts-owed-to-you')?.value) || 0;
  const liabilities = parseFloat(byId('zakat-debts-you-owe')?.value) || 0;
  const calendarType = byId('zakat-calendar-type')?.value || 'lunar';

  const res = calculateZakat({
    cash,
    gold24k,
    gold21k,
    gold18k,
    gold24kPrice,
    silverGrams,
    silverPrice,
    tradeInventory,
    receivables,
    liabilities,
    calendarType
  });

  if (byId('zakat-due-result')) {
    byId('zakat-due-result').textContent = formatCurrency(res.zakatDue, currency);
  }
  if (byId('zakat-net-assets')) {
    byId('zakat-net-assets').textContent = formatCurrency(res.netZakatable, currency);
  }
  if (byId('zakat-nisab-val')) {
    byId('zakat-nisab-val').textContent = formatCurrency(res.nisab, currency);
  }

  const badgeEl = byId('zakat-eligibility-badge');
  if (badgeEl) {
    if (res.isEligible) {
      badgeEl.textContent = isAr ? 'بلغت النصاب الشرعي (تجب الزكاة)' : 'Nisab Reached (Zakat Due)';
      badgeEl.className = 'kz-badge kz-badge-success';
    } else {
      badgeEl.textContent = isAr ? 'دون النصاب الشرعي (لا تجب الزكاة)' : 'Below Nisab (No Zakat Due)';
      badgeEl.className = 'kz-badge kz-badge-warning';
    }
  }

  if (byId('zakat-gold-total-val')) {
    byId('zakat-gold-total-val').textContent = formatCurrency(res.goldValue, currency);
  }
  if (byId('zakat-rate-label')) {
    byId('zakat-rate-label').textContent = `${res.ratePercent}% (${calendarType === 'solar' ? (isAr ? 'حَوْل شمسي' : 'Solar Year') : (isAr ? 'حَوْل قمري / هجري' : 'Lunar Year')})`;
  }

  const tafgeetEl = byId('zakat-tafgeet-val');
  if (tafgeetEl) {
    if (res.zakatDue > 0) {
      tafgeetEl.textContent = getInvoiceTafgeet(res.zakatDue.toFixed(2), currency);
    } else {
      tafgeetEl.textContent = '';
    }
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initZakatCalculator);
  } else {
    initZakatCalculator();
  }
}
